import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { fmtINR, fyStart, todayStr } from '../utils';

const API   = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('bc_token') || '';

function fmtAmt(n) { if (n == null) return ''; return fmtINR(n) || ''; }
function parseBal(raw) {
  if (!raw || Math.abs(raw) < 0.005) return { label:'₹0', side:'', color:'var(--text3)' };
  return { label:fmtAmt(raw), side:raw<0?'Dr':'Cr', color:raw<0?'var(--blue)':'var(--purple)' };
}
const TYPE_COLOR = {
  'Sale':'var(--green)','Receipt':'var(--blue)','Purchase Return':'var(--green)',
  'Contra':'var(--purple)','Purchase':'var(--red)','Payment':'var(--amber)',
  'Sale Return':'var(--red)','Journal':'var(--amber)','Debit Note':'var(--amber)','Credit Note':'var(--amber)',
};

const PFX = 'lg_';
function getP(key) { return new URLSearchParams(window.location.search).get(PFX + key) || ''; }
function setP(key, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = val || '';
  if (v && v !== 'All') sp.set(PFX + key, v); else sp.delete(PFX + key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

export default function Ledger({ db }) {
  const [view,   setView]   = useState(() => getP('code') ? 'detail' : 'list');
  const [ledger, setLedger] = useState(() => {
    const code = getP('code'), name = getP('name');
    return code ? { Code:code, Name:decodeURIComponent(name) } : null;
  });

  function openDetail(l) {
    setP('code', l.Code); setP('name', encodeURIComponent(l.Name));
    setP('fr', ''); setP('to', ''); setP('ds', '');
    setLedger(l); setView('detail');
  }
  function backToList() {
    setP('code', ''); setP('name', '');
    setP('fr', '');   setP('to', ''); setP('ds', '');
    setView('list');
  }

  return view === 'detail'
    ? <LedgerDetail db={db} ledger={ledger} onBack={backToList} />
    : <LedgerList   db={db} onSelect={openDetail} />;
}

/* ── LIST ──────────────────────────────────────────────── */
function LedgerList({ db, onSelect }) {
  const [ledgers,   setLedgers]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const [search,    setSearchRaw]    = useState(() => getP('ls'));
  const [grpFilter, setGrpFilterRaw] = useState(() => getP('lf') || 'All');
  const [hideZero,  setHideZeroRaw]  = useState(() => getP('hz') === '1');

  const setSearch    = v => { setSearchRaw(v);    setP('ls', v); };
  const setGrpFilter = v => { setGrpFilterRaw(v); setP('lf', v); };
  const setHideZero  = v => { setHideZeroRaw(v);  setP('hz', v ? '1' : ''); };

  useEffect(() => {
    if (!db) return;
    setLoading(true); setError(null);
    fetch(`${API}/api/ledger-list?db=${encodeURIComponent(db)}`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(j => setLedgers(j.data?.ledgers || j.ledgers || []))
      .catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [db]);

  const allGroups = useMemo(() => ['All', ...new Set(ledgers.map(l => l.Group2))], [ledgers]);

  const filtered = useMemo(() => {
    let rows = ledgers;
    if (grpFilter !== 'All') rows = rows.filter(l => l.Group2 === grpFilter);
    if (hideZero) rows = rows.filter(l => Math.abs(l.ClosingBalance || 0) >= 0.005);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(l =>
        l.Name.toLowerCase().includes(q) ||
        l.Group1.toLowerCase().includes(q) ||
        l.Group2.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [ledgers, grpFilter, hideZero, search]);

  const categories = useMemo(() => {
    const map = {};
    for (const l of filtered) {
      const key = `${l.Group2}||${l.Group1}`;
      if (!map[key]) map[key] = { Group2:l.Group2, Group1:l.Group1, items:[] };
      map[key].items.push(l);
    }
    return Object.values(map).sort((a,b) =>
      a.Group2.localeCompare(b.Group2) || a.Group1.localeCompare(b.Group1));
  }, [filtered]);

  function toggle(key) { setCollapsed(c => ({ ...c, [key]: !c[key] })); }
  const withBal = useMemo(() => filtered.filter(l => Math.abs(l.ClosingBalance || 0) >= 0.005).length, [filtered]);

  const anyFilter = grpFilter !== 'All' || !!search.trim() || hideZero;
  function clearAll() {
    setSearchRaw(''); setGrpFilterRaw('All'); setHideZeroRaw(false);
    setP('ls', ''); setP('lf', ''); setP('hz', '');
  }

  return (
    <div className="page-wrap fade-up">
      <div className="page-head">
        <div className="page-title">Ledger</div>
        <div className="page-sub">All accounts · {db}</div>
      </div>

      <div className="lg-search-bar">
        <div className="search-box" style={{ maxWidth:'100%', flex:1 }}>
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input placeholder="Search ledger, group…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button
          className={`db-chip${hideZero ? ' db-chip-active' : ''}`}
          onClick={() => setHideZero(!hideZero)}
          title="0 wale ledger hata do"
        >
          {hideZero ? '✓ ' : ''}0 wale hatao
        </button>
        <button className="btn-reset" onClick={clearAll} disabled={!anyFilter}>↺ Reset</button>
        <span className="count-badge">{filtered.length} · {withBal} with balance</span>
      </div>

      <div className="db-chips-row" style={{ marginBottom:10 }}>
        {allGroups.map(g => (
          <button key={g} className={`db-chip${grpFilter === g ? ' db-chip-active' : ''}`}
            onClick={() => setGrpFilter(g)}>{g}</button>
        ))}
      </div>

      {loading && <div className="loading-state"><div className="spinner" /><span>Loading ledgers…</span></div>}
      {error   && <div className="error-bar"><span className="error-icon">⚠</span><div>{error}</div></div>}

      {!loading && categories.map(cat => {
        const key    = `${cat.Group2}||${cat.Group1}`;
        const isOpen = !collapsed[key];
        const total  = cat.items.reduce((s, l) => s + (l.ClosingBalance || 0), 0);
        const catBal = parseBal(total);
        return (
          <div key={key} className="lg-cat-block">
            <div className="lg-cat-header" onClick={() => toggle(key)}>
              <span className="lg-cat-arrow">{isOpen ? '▾' : '▸'}</span>
              <span className="lg-cat-g2">{cat.Group2}</span>
              <span className="lg-cat-sep">›</span>
              <span className="lg-cat-g1">{cat.Group1}</span>
              <span className="lg-cat-count">{cat.items.length}</span>
              <div style={{ flex:1 }} />
              <span className="lg-cat-bal" style={{ color:catBal.color }}>
                {catBal.label} <small>{catBal.side}</small>
              </span>
            </div>
            {isOpen && (
              <div className="lg-cat-body">
                {cat.items.map(l => {
                  const b = parseBal(l.ClosingBalance);
                  return (
                    <div key={l.Code} className="lg-row" onClick={() => onSelect(l)}>
                      <span className="lg-row-code">#{l.Code}</span>
                      <span className="lg-row-name">{l.Name}</span>
                      <span className="lg-row-bal" style={{ color:b.color }}>{b.label} <small>{b.side}</small></span>
                      <span className="lg-row-arrow">›</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {!loading && !error && categories.length === 0 && (
        <div className="empty-state"><div className="empty-icon">📊</div><h3>No ledgers found</h3></div>
      )}
    </div>
  );
}

/* ── DETAIL ────────────────────────────────────────────── */
function LedgerDetail({ db, ledger, onBack }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Dates as refs so fetchDetail doesn't need them as dependencies
  // This prevents auto-fetch on every keystroke / date change
  const [fromDate, setFromDateRaw] = useState(() => getP('fr') || fyStart());
  const [toDate,   setToDateRaw]   = useState(() => getP('to') || todayStr());
  const [search,   setSearchRaw]   = useState(() => getP('ds'));

  const fromRef = useRef(fromDate);
  const toRef   = useRef(toDate);

  const setFromDate = v => { setFromDateRaw(v); fromRef.current = v; setP('fr', v); };
  const setToDate   = v => { setToDateRaw(v);   toRef.current   = v; setP('to', v); };
  const setSearch   = v => { setSearchRaw(v);   setP('ds', v); };

  // fetchDetail reads dates from refs — NEVER re-created due to date changes
  // Only recreated when db/ledger changes (i.e. opening a different ledger)
  const fetchDetail = useCallback(async () => {
    if (!db || !ledger?.Code) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `${API}/api/ledger-detail?db=${encodeURIComponent(db)}&code=${ledger.Code}&from=${fromRef.current}&to=${toRef.current}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Request failed');
      setData(j.data || j);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [db, ledger]); // ← NO fromDate/toDate here — read from refs instead

  // Auto-load only on mount / ledger change — NOT on date change
  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const entries = useMemo(() => {
    if (!data?.entries) return [];
    let running = data.openingBalance || 0;
    let rows = data.entries.map(e => { running += e.RawValue; return { ...e, RunningBalance:running }; });
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        (r.Particulars||'').toLowerCase().includes(q) ||
        (r.VchNo||'').toLowerCase().includes(q) ||
        (r.VchType||'').toLowerCase().includes(q) ||
        (r.Narration||'').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, search]);

  const openBal    = parseBal(data?.openingBalance);
  const closingRaw = data ? (data.openingBalance||0) + (data.entries||[]).reduce((s,e) => s+e.RawValue, 0) : null;
  const closeBal   = closingRaw != null ? parseBal(closingRaw) : null;
  const listBal    = parseBal(ledger?.ClosingBalance);

  function clearSearch() { setSearchRaw(''); setP('ds', ''); }

  return (
    <div className="page-wrap fade-up">
      <div className="lg-detail-head">
        <button className="lg-back-btn" onClick={onBack}>← Back</button>
        <div className="lg-detail-title-wrap">
          <div className="lg-detail-name">{ledger?.Name}</div>
          <div className="lg-detail-meta">
            {ledger?.Group2} › {ledger?.Group1}
            <span style={{ marginLeft:8, fontFamily:'var(--mono)' }}>#{ledger?.Code}</span>
            <span style={{ marginLeft:10 }}>
              FY:&nbsp;<span style={{ color:listBal.color, fontFamily:'var(--mono)' }}>{listBal.label} {listBal.side}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Date bar — Load button triggers fetch explicitly */}
      <div className="lg-date-bar">
        <div className="lg-date-inputs">
          <div className="lg-date-group">
            <label>From</label>
            <input type="date" className="filter-input" value={fromDate}
              onChange={e => setFromDate(e.target.value)} />
          </div>
          <div className="lg-date-group">
            <label>To</label>
            <input type="date" className="filter-input" value={toDate}
              onChange={e => setToDate(e.target.value)} />
          </div>
          {/* Explicit Load button — only way to trigger fetch after date change */}
          <button className="btn-load" onClick={fetchDetail} disabled={loading}>
            {loading
              ? <div className="spinner" style={{ width:13, height:13 }} />
              : <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                  </svg>
                  Load
                </>}
          </button>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flex:1, minWidth:0 }}>
          <div className="search-box" style={{ flex:1, minWidth:0 }}>
            <svg className="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input placeholder="Search entries…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn-reset" onClick={clearSearch} disabled={!search.trim()}>↺ Reset</button>
        </div>
      </div>

      {data && (
        <div className="lg-bal-row">
          <div className="lg-bal-pill">
            <span className="lg-bal-pill-label">Opening</span>
            <span className="lg-bal-pill-val" style={{ color:openBal.color }}>
              {openBal.label} <small>{openBal.side}</small>
            </span>
          </div>
          {closeBal && (
            <div className="lg-bal-pill lg-bal-pill-bold">
              <span className="lg-bal-pill-label">Closing</span>
              <span className="lg-bal-pill-val" style={{ color:closeBal.color }}>
                {closeBal.label} <small>{closeBal.side}</small>
              </span>
            </div>
          )}
          <span className="count-badge" style={{ alignSelf:'center' }}>{data.count ?? data.entries?.length} entries</span>
        </div>
      )}

      {error   && <div className="error-bar"><span className="error-icon">⚠</span><div><div>Failed to load</div><div className="error-detail">{error}</div></div></div>}
      {loading && <div className="loading-state"><div className="spinner" /><span>Loading entries…</span></div>}

      {!loading && data && (
        <div className="table-wrap page-table-wrap">
          <table className="data-table" style={{ minWidth:700 }}>
            <thead>
              <tr>
                <th style={{ width:82 }}>Date</th>
                <th style={{ width:82 }}>Type</th>
                <th style={{ width:110 }}>Voucher</th>
                <th>Particulars</th>
                <th className="num-col" style={{ width:105 }}>Debit</th>
                <th className="num-col" style={{ width:105 }}>Credit</th>
                <th className="num-col" style={{ width:120 }}>Balance</th>
                <th style={{ width:140 }}>Narration</th>
              </tr>
            </thead>
            <tbody>
              <tr className="lg-opening-row">
                <td colSpan={6} style={{ fontSize:12, color:'var(--text3)', fontStyle:'italic' }}>
                  Opening Balance — {fromDate}
                </td>
                <td className="num-col" style={{ fontFamily:'var(--mono)', color:openBal.color, fontWeight:600 }}>
                  {openBal.label} <small style={{ fontSize:9, opacity:0.8 }}>{openBal.side}</small>
                </td>
                <td />
              </tr>
              {entries.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--text3)', padding:32 }}>
                  No transactions in this range
                </td></tr>
              )}
              {entries.map((e, i) => {
                const rb = parseBal(e.RunningBalance);
                return (
                  <tr key={`${e.VchCode}-${e.SrNo}-${i}`}>
                    <td style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{e.VoucherDate}</td>
                    <td style={{ fontSize:12, fontWeight:600, color:TYPE_COLOR[e.VchType]||'var(--text3)', whiteSpace:'nowrap' }}>{e.VchType}</td>
                    <td style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{e.VchNo || '—'}</td>
                    <td style={{ maxWidth:200 }}>
                      {e.Particulars
                        ? (e.Particulars.length>55 ? e.Particulars.slice(0,55)+'…' : e.Particulars)
                        : <span className="muted">—</span>}
                    </td>
                    <td className="num-col" style={{ color:'var(--blue)' }}>{e.DebitAmt  != null ? fmtAmt(e.DebitAmt)  : ''}</td>
                    <td className="num-col" style={{ color:'var(--purple)' }}>{e.CreditAmt != null ? fmtAmt(e.CreditAmt) : ''}</td>
                    <td className="num-col" style={{ color:rb.color, fontWeight:600 }}>
                      {rb.label} <small style={{ fontSize:9, opacity:0.8 }}>{rb.side}</small>
                    </td>
                    <td style={{ fontSize:12, color:'var(--text3)' }}>{e.Narration || ''}</td>
                  </tr>
                );
              })}
              {closeBal && (
                <tr className="lg-closing-row">
                  <td colSpan={6} style={{ fontSize:12, color:'var(--text3)', fontStyle:'italic' }}>
                    Closing Balance — {toDate}
                  </td>
                  <td className="num-col" style={{ color:closeBal.color, fontWeight:700 }}>
                    {closeBal.label} <small style={{ fontSize:9, opacity:0.8 }}>{closeBal.side}</small>
                  </td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
